import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import jwt_decode from "jwt-decode";

import LOCAL_STORAGE from "@/constants/local-storage";
import { logSessionEvent } from "@/utils/session-log";
import { getAccessToken, getCustomerId, isImpersonationSession } from "@/helpers/auth-helpers";
import { IDecodedToken } from "@/interfaces/auth.interfaces";
import { isTokenExpired } from "@/utils/token-utils";

const apiBaseUrl: string = import.meta.env.VITE_API_URL || "https://api-qas.talk2buy.com.br";
const apiBackofficeBaseUrl: string =
  import.meta.env.VITE_API_BACKOFFICE_URL || "https://api-backoffice-qas.talk2buy.com.br";
const apiMetaBaseUrl: string =
  import.meta.env.VITE_META_API_URL || "https://graph.facebook.com/v18.0";

const metaAccessToken = import.meta.env.VITE_META_T2B_ACCESS_TOKEN || "";

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (error?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

export async function refreshAccessToken(refreshToken: string): Promise<string | null> {
  try {
    const response = await axios.post(`${apiBaseUrl}/api/v1/login/refreshtoken`, {
      refreshToken,
    });

    const { refreshToken: newRefreshToken, accessToken: newAccessToken } = response.data;

    if (!newRefreshToken || !newAccessToken) {
      return null;
    }

    localStorage.setItem(LOCAL_STORAGE.accessTokenKey, newAccessToken);
    localStorage.setItem(LOCAL_STORAGE.refreshTokenKey, newRefreshToken);

    return newAccessToken;
  } catch (error) {
    console.error("Error refreshing access token:", error);
    return null;
  }
}

function forceLogout(reason = "desconhecido") {
  logSessionEvent(`forceLogout: ${reason}`);
  localStorage.removeItem(LOCAL_STORAGE.accessTokenKey);
  localStorage.removeItem(LOCAL_STORAGE.refreshTokenKey);
  window.dispatchEvent(new CustomEvent("tokenExpired"));

  const currentPath = window.location.pathname;
  const isPublicRoute =
    currentPath.includes("/login") ||
    currentPath.includes("/register") ||
    currentPath.includes("/signup") ||
    currentPath.includes("/terms") ||
    currentPath.includes("/privacy");

  if (!isPublicRoute) {
    window.location.href = "/login";
  }
}

export const apiNext = axios.create({
  baseURL: "/api",
});

apiNext.interceptors.request.use(
  (config) => {
    const accessToken = getAccessToken();
    const customerId = getCustomerId();

    if (accessToken && customerId) {
      config.headers.accessToken = accessToken;
      config.headers.CustomerId = customerId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiNext.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => Promise.reject(error)
);

export const apiT2B = axios.create({
  baseURL: apiBaseUrl,
});

apiT2B.interceptors.request.use(
  async (config) => {
    const isAuthRoute =
      config.url?.includes("/login") ||
      config.url?.includes("/register") ||
      config.url?.includes("/signup");
    const isInternalLogin = config.data?.login && config.data?.password;
    const isRecaptchaRoute = config.url?.includes("/recaptcha");

    if ((isAuthRoute && !isInternalLogin) || isRecaptchaRoute) {
      return config;
    }

    if (config.headers && (config.headers as Record<string, unknown>).Authorization) {
      return config;
    }

    let accessToken = getAccessToken();
    const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

    if (!accessToken) {
      if (refreshToken && !isAuthRoute) {
        const newAccessToken = await refreshAccessToken(refreshToken);
        if (newAccessToken) {
          accessToken = newAccessToken;
        } else {
          forceLogout("request: refresh falhou sem accessToken");
          return Promise.reject(new AxiosError("No authentication token", undefined, config));
        }
      } else if (!isAuthRoute) {
        forceLogout("request: sem accessToken e sem refreshToken");
        return Promise.reject(new AxiosError("No authentication token", undefined, config));
      } else {
        return config;
      }
    }

    try {
      let decodedToken: IDecodedToken = jwt_decode(accessToken);
      const isImpersonation = isImpersonationSession(decodedToken);

      // Tokens de impersonation não devem ser renovados
      if (isTokenExpired(decodedToken)) {
        if (isImpersonation) {
          forceLogout("request: impersonation expirada");
          return Promise.reject(new AxiosError("Impersonation session expired", undefined, config));
        }

        if (refreshToken) {
          const newAccessToken = await refreshAccessToken(refreshToken);
          if (newAccessToken) {
            accessToken = newAccessToken;
            decodedToken = jwt_decode(newAccessToken);
          }
        }
      }

      if (!isTokenExpired(decodedToken) && accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
        config.headers.CustomerId = decodedToken.CustomerId || "";
      }
    } catch {
      // Não forçar logout em decode inválido — alinhado ao Backoffice / http-client
      return Promise.reject(new AxiosError("Invalid token", undefined, config));
    }

    return config;
  },
  (error) => Promise.reject(error)
);

apiT2B.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (!error.response || error.response.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    if (originalRequest.url?.includes("/refreshtoken")) {
      forceLogout("response: 401 na chamada de refreshtoken");
      return Promise.reject(error);
    }

    // Impersonation: nunca tenta refresh. Se o token ainda é válido, 401 de permissão
    // (UserAccess) não deve encerrar a sessão — isso reflete a visão real do perfil.
    try {
      const accessToken = getAccessToken();
      if (accessToken) {
        const decodedToken: IDecodedToken = jwt_decode(accessToken);
        if (isImpersonationSession(decodedToken)) {
          if (isTokenExpired(decodedToken)) {
            forceLogout("response: impersonation expirada em 401");
          }
          return Promise.reject(error);
        }
      }
    } catch {
      // Decode inválido: segue para fluxo de refresh / logout
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return apiT2B(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

    if (!refreshToken) {
      processQueue(error, null);
      isRefreshing = false;
      forceLogout("response: 401 sem refreshToken");
      return Promise.reject(error);
    }

    const newAccessToken = await refreshAccessToken(refreshToken);

    if (!newAccessToken) {
      processQueue(error, null);
      isRefreshing = false;
      forceLogout("response: refresh falhou apos 401");
      return Promise.reject(error);
    }

    processQueue(null, newAccessToken);
    isRefreshing = false;

    if (originalRequest.headers) {
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
    }

    return apiT2B(originalRequest);
  }
);

export const apiBackofficeT2b = axios.create({
  baseURL: apiBackofficeBaseUrl,
});

export const apiMeta = axios.create({
  baseURL: apiMetaBaseUrl,
  headers: {
    Authorization: `Bearer ${metaAccessToken}`,
  },
});

apiMeta.interceptors.request.use(
  (config) => {
    config.headers.Authorization = `Bearer ${metaAccessToken}`;
    return config;
  },
  (error) => Promise.reject(error)
);

export const apiGupShup = axios.create({
  baseURL: "https://partner.gupshup.io/partner",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/x-www-form-urlencoded",
  },
});
